/**
 * 牌桌权威状态（§11.3）。
 *
 * 这里只放「真源」：脱敏后的手牌视图 + 座位元信息 + 连接相位。
 * **派生的渲染模型（TableViewModel）不进 store** —— 它是 view 的纯函数，
 * 存进 store 只会制造两份可能不一致的真相，并且每次 selector 命中都要做深比较。
 * 渲染层用 `useMemo(() => buildTableVM(view, heroSeat), [view, heroSeat])` 派生即可。
 *
 * 三种形态（联机 WS / 本地传手机 / 回放）都往这里写，Transport 之上不出现 mode 分支（§11.7）。
 */

import { create } from 'zustand'
import { SEAT_LIMITS, type ActionIntent, type Chips, type LegalActions, type PublicHandState, type RoomConfig, type SeatIndex } from '@poker/shared'

import { layoutSeatCount } from '../table/seatGeometry.js'

export type ConnectionPhase =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'syncing'
  | 'live'
  | 'degraded'
  | 'backoff'
  | 'superseded'
  | 'fatal'

/**
 * 本地模式下 name 来自 LocalPlayer，联机模式来自房间成员。
 * `avatarSeed` / `accountId` 只有联机模式才有（跟着 `PublicPlayer` 走）——本地传手机
 * 玩家没有账号，两个字段都留空，`resolveAvatar` 空字符串一律按「没有自定义头像」处理。
 * 两个都是原始字符串而不是提前解出来的一个对象：`sameSeats` 的浅比较靠的就是
 * 「每个字段都能用 `!==` 直接比」，换成对象要么每帧新对象误判成「变了」，
 * 要么还得在 diff 里手写一遍解构比较——不如让 `Seat.tsx` 自己按需解。
 */
export interface TableSeatInfo {
  readonly seat: SeatIndex
  readonly name: string
  readonly stack: Chips
  readonly isBot: boolean
  /** 座位上的人为什么不在牌局里（断线 / 暂离 / 筹码用完 / 买分中）；正常在座为 null */
  readonly note: SeatNote | null
  readonly avatarSeed: string
  readonly accountId: string
}

/**
 * 座位角标说的那件事。四种状态看起来都是「这人这手不打」，但原因和该做的事完全不同：
 *   offline = 断线（人不在）             → 危险色小胶囊「离线」
 *   away    = 暂离，手里还有筹码         → 中性胶囊「暂离」，他自己点「回到牌局」就回来
 *   broke   = 筹码用完，还没申请买分     → 中性胶囊「筹码用完」
 *   buying  = 筹码用完，买分待批 / 已批待到账 → 中性胶囊「买分中」
 * ★ 以前后三种一律画成和「离线」同一个红色胶囊，输光了在等买分的 AI 看起来就像掉线了。
 */
export type SeatNote = 'offline' | 'away' | 'broke' | 'buying'

export interface TableState {
  readonly roomCode: string | null
  readonly config: RoomConfig | null
  readonly seats: readonly TableSeatInfo[]
  /**
   * 观察视角的座位号。本地传手机模式下它会随「轮到谁」切换，
   * 切换时必须同时 veil + 重建 equity worker（§11.8 第 10 条）。
   */
  readonly heroSeat: SeatIndex | null
  readonly view: PublicHandState | null
  readonly legal: LegalActions | null
  readonly connection: ConnectionPhase
  /**
   * 参与排布的座位集合。手牌进行中**冻结**（§11.4）：
   * 否则一手牌打到一半来人就整桌漂移，筹码飞行的起止点跟着变。
   */
  readonly layoutSeats: readonly SeatIndex[]
  /** 已提交但还没被引擎/服务端确认的意图，用于按钮置灰 */
  readonly pending: ActionIntent | null
  readonly error: string | null

  setRoom(roomCode: string | null, config: RoomConfig | null): void
  setSeats(seats: readonly TableSeatInfo[]): void
  setHeroSeat(seat: SeatIndex | null): void
  setConnection(phase: ConnectionPhase): void
  applyHandView(view: PublicHandState | null, legal?: LegalActions | null): void
  setPending(intent: ActionIntent | null): void
  setError(message: string | null): void
  reset(): void
}

/** 手牌不在进行中：没有 view，或已经结算完。座位重排只能在这种时候发生。 */
export function isTableIdle(view: PublicHandState | null): boolean {
  return view === null || view.phase === 'complete'
}

/** 当前应参与排布的座位号（已入座的；不足 2 人时补足到 2 个占位） */
function computeLayoutSeats(seats: readonly TableSeatInfo[], maxSeats: number): SeatIndex[] {
  const occupied = seats.map((s) => s.seat).sort((a, b) => a - b)
  const want = layoutSeatCount(occupied.length, maxSeats)
  if (occupied.length >= want) return occupied.slice(0, want)
  // 空桌 / 单人：补占位座位号，保证牌桌不会塌成一个点
  const out = [...occupied]
  for (let s = 0; out.length < want && s < SEAT_LIMITS.MAX; s++) {
    if (!out.includes(s)) out.push(s)
  }
  return out.sort((a, b) => a - b)
}

const EMPTY: Pick<
  TableState,
  'roomCode' | 'config' | 'seats' | 'heroSeat' | 'view' | 'legal' | 'connection' | 'layoutSeats' | 'pending' | 'error'
> = {
  roomCode: null,
  config: null,
  seats: [],
  heroSeat: null,
  view: null,
  legal: null,
  connection: 'idle',
  layoutSeats: [0, 1],
  pending: null,
  error: null,
}

export const useTableStore = create<TableState>((set, get) => ({
  ...EMPTY,

  setRoom: (roomCode, config) => {
    set({ roomCode, config })
    const s = get()
    if (isTableIdle(s.view)) {
      set({ layoutSeats: computeLayoutSeats(s.seats, config?.maxSeats ?? SEAT_LIMITS.MAX) })
    }
  },

  setSeats: (seats) => {
    const s = get()
    // 冻结规则：手牌进行中新入座者先占用现有 slot，phase 回 idle 才重排 + FLIP
    if (isTableIdle(s.view)) {
      set({ seats, layoutSeats: computeLayoutSeats(seats, s.config?.maxSeats ?? SEAT_LIMITS.MAX) })
    } else {
      set({ seats })
    }
  },

  setHeroSeat: (heroSeat) => set({ heroSeat }),
  setConnection: (connection) => set({ connection }),

  applyHandView: (view, legal = null) => {
    const s = get()
    const wasIdle = isTableIdle(s.view)
    const nowIdle = isTableIdle(view)
    // 新的一手开始时（idle -> 非 idle）把布局锁死在当时的座位集合上
    const layoutSeats =
      wasIdle && !nowIdle
        ? computeLayoutSeats(s.seats, s.config?.maxSeats ?? SEAT_LIMITS.MAX)
        : nowIdle
          ? computeLayoutSeats(s.seats, s.config?.maxSeats ?? SEAT_LIMITS.MAX)
          : s.layoutSeats
    set({ view, legal, layoutSeats, pending: null })
  },

  setPending: (pending) => set({ pending }),
  setError: (error) => set({ error }),
  reset: () => set({ ...EMPTY }),
}))

// ============================================================================
// 精确订阅切片（§11.3）—— 组件订阅这些而不是整个 store，避免全表重渲染
// ============================================================================

export const selectView = (s: TableState): PublicHandState | null => s.view
export const selectHeroSeat = (s: TableState): SeatIndex | null => s.heroSeat
export const selectLayoutSeats = (s: TableState): readonly SeatIndex[] => s.layoutSeats
export const selectConnection = (s: TableState): ConnectionPhase => s.connection
export const selectLegal = (s: TableState): LegalActions | null => s.legal
export const selectActionOn = (s: TableState): SeatIndex | null => s.view?.actionOn ?? null
export const selectPotTotal = (s: TableState): Chips => s.view?.potTotal ?? 0
export const selectIsHeroTurn = (s: TableState): boolean =>
  s.heroSeat !== null && s.view?.actionOn === s.heroSeat
